'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from '@/components/public/i18n/LocalizedLink';
import { useI18n } from '@/components/public/i18n/LocaleProvider';
import { stripLocale } from '@/lib/i18n/core';
import { RNODE_CASES, RNODE_CASES_URL } from '@/lib/public/rnode-cases';
import { UNIVERSES } from '@/lib/public/universes';
import './navigation.css';

export default function PublicNavigation() {
  const { t, href } = useI18n();
  const pathname = stripLocale(usePathname() || '/');
  const [mobile, setMobile] = useState(false);
  const [expanded, setExpanded] = useState<'solutions' | 'metiers' | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const mobileButton = useRef<HTMLButtonElement>(null);
  const solutionButton = useRef<HTMLButtonElement>(null);
  const sectorButton = useRef<HTMLButtonElement>(null);
  const close = () => { setMobile(false); setExpanded(null); };
  useEffect(() => { close(); }, [pathname]);
  useEffect(() => {
    const outside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) close(); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, []);
  return <div className="eco-navigation" ref={root} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) close(); }} onKeyDown={event => {
    if (event.key === 'Escape') {
      if (expanded) { (expanded === 'solutions' ? solutionButton : sectorButton).current?.focus(); setExpanded(null); }
      else { setMobile(false); mobileButton.current?.focus(); }
    }
  }}>
    <button className="eco-mobile-toggle" ref={mobileButton} aria-controls="public-navigation" aria-expanded={mobile} onClick={() => setMobile(!mobile)} type="button">{t(mobile ? 'Fermer' : 'Menu')}<span aria-hidden="true">{mobile ? '×' : '☰'}</span></button>
    <nav id="public-navigation" className={`eco-site-nav${mobile ? ' is-open' : ''}`} aria-label={t('Navigation principale')}>
      <Link href="/ecosysteme" aria-current={pathname === '/ecosysteme' ? 'page' : undefined} onClick={close}>{t('Écosystème')}</Link>
      <Link href="/methode-sx" aria-current={pathname === '/methode-sx' ? 'page' : undefined} onClick={close}>{t('Méthode SX')}</Link>
      <div className="eco-nav-group"><button ref={solutionButton} type="button" aria-expanded={expanded === 'solutions'} aria-controls="public-solutions" onClick={() => setExpanded(expanded === 'solutions' ? null : 'solutions')}>{t('Solutions')}<span aria-hidden="true">⌄</span></button>
        <div id="public-solutions" className="eco-dropdown" hidden={expanded !== 'solutions'}>
          {UNIVERSES.map(universe => <Link href={`/${universe.id}`} key={universe.id} aria-current={pathname === '/' + universe.id ? 'page' : undefined} onClick={close}><strong>{universe.id === 'elina' ? 'Élina' : universe.name}</strong><small>{t(universe.descriptor)}</small></Link>)}
          <Link href="/r-node/focus" onClick={close}><strong>{t('Focus R-Node')}</strong><small>{t('Comprendre les modules')}</small></Link>
        </div>
      </div>
      <div className="eco-nav-group"><button ref={sectorButton} type="button" aria-expanded={expanded === 'metiers'} aria-controls="public-sectors" onClick={() => setExpanded(expanded === 'metiers' ? null : 'metiers')}>{t('Votre métier')}<span aria-hidden="true">⌄</span></button>
        <div id="public-sectors" className="eco-dropdown eco-sector-dropdown" hidden={expanded !== 'metiers'}>
          {RNODE_CASES.map(item => <a key={item.id} href={href(`${RNODE_CASES_URL}/${item.id}/`)} onClick={close}><strong>{t(item.label)}</strong><small>{t(item.tag)}</small></a>)}
          <a href={href(`${RNODE_CASES_URL}/#metiers`)} onClick={close}><strong>{t('Tous les cas pratiques R-Node')} ↗</strong></a>
        </div>
      </div>
      <Link href="/a-propos" aria-current={pathname === '/a-propos' ? 'page' : undefined} onClick={close}>{t('À propos')}</Link>
    </nav>
  </div>;
}
